import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Button, Spinner, Alert, Form, Row, Col, InputGroup, Card, Badge, Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaSearch, FaTrash, FaArrowLeft, FaVideo, FaFileAlt, FaUserAlt, FaClock } from 'react-icons/fa';
import { getAppliedRoleListForDrCr, fetchUserJogetProfileApi } from '../../../services/api'; 
import MetaTags from '../../../components/MetaTags';
import './ManageApplicationsPage.css';

const ManageApplicationsPage = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    
    // --- VIEW MODE STATES ---
    const [viewMode, setViewMode] = useState(false);
    const [selectedApp, setSelectedApp] = useState(null);
    const [talentProfile, setTalentProfile] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [decision, setDecision] = useState('');

    const navigate = useNavigate();

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await getAppliedRoleListForDrCr();
            const rawData = response.data.data || [];

            const transformedData = rawData.map(item => {
                let appDetails = {};
                try {
                    const parsed = JSON.parse(item.roleApplicationJson);
                    appDetails = parsed[0] || {};
                } catch (e) { console.error(e); }

                let c_B_Month = '202401';
                let c_S_Date = '01/01/2024';
                try {
                    const roleSchedules = JSON.parse(item.roleScheduleJson);
                    if (Array.isArray(roleSchedules) && roleSchedules.length > 0 && roleSchedules[0].c_startDate) {
                        const startDate = new Date(roleSchedules[0].c_startDate);
                        if (!isNaN(startDate)) {
                            c_B_Month = `${startDate.getFullYear()}${(startDate.getMonth() + 1).toString().padStart(2, '0')}`;
                            c_S_Date = `${startDate.getDate().toString().padStart(2, '0')}/${(startDate.getMonth() + 1).toString().padStart(2, '0')}/${startDate.getFullYear()}`;
                        }
                    }
                } catch (e) { console.error(e); }

                return {
                    ...item,
                    talent_name: item.candidateName || '',
                    c_role_name: item.c_roleName,
                    c_project_name: item.projectName,
                    talent_id: item.appliedByUser,
                    c_R_Status: appDetails.c_result || 'Pending',
                    c_B_Month: c_B_Month,
                    c_S_Date: c_S_Date,
                    c_V_Status: item.c_approval_status || 'Pending',
                    c_audition_type: item.bookingType || 'N/A'
                };
            });

            setData(transformedData);
        } catch (err) {
            setError('Failed to load application data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const handleViewApp = async (app) => {
        setSelectedApp(app);
        setViewMode(true);
        setProfileLoading(true);
        try {
            // ✅ Fetch Complete Talent Data from Backend
            const res = await fetchUserJogetProfileApi(app.talent_id);
            setTalentProfile(res.data.jogetData);
        } catch (e) {
            console.error("Profile load error:", e);
        } finally {
            setProfileLoading(false);
        }
    };

    const handleUpdateStatus = () => {
        if(!decision) return alert("Please select a result");
        alert(`Status updated to: ${decision}`);
        setViewMode(false);
        loadData();
    };

    const filteredData = useMemo(() => {
        return data.filter(item => 
            item.c_role_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.talent_name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [data, searchTerm]);

    const totalPages = Math.ceil(filteredData.length / entriesPerPage);
    const currentData = filteredData.slice((currentPage - 1) * entriesPerPage, currentPage * entriesPerPage);

    if (loading) return <div className="text-center p-5"><Spinner animation="border" variant="danger" /></div>;

    // --- RENDER APPLICATION REVIEW WIZARD ---
    if (viewMode && selectedApp) {
        const steps = ['Application', 'Script', 'Media', 'Finalize'];
        const tp = talentProfile || {};

        return (
            <div className="review-wizard-overlay animate-fade-in">
                <MetaTags title={`Reviewing - ${selectedApp.talent_name}`} />
                <div className="d-flex align-items-center mb-4">
                    <Button variant="outline-light" size="sm" onClick={() => setViewMode(false)} className="me-3"><FaArrowLeft /> Back</Button>
                    <h5 className="text-white m-0 text-uppercase fw-bold">Audition Review Panel</h5>
                </div>

                <Row className="g-4">
                    {/* LEFT SIDE: TALENT SIDEBAR (4-COLUMN GRID) */}
                    <Col lg={5}>
                        <div className="talent-sidebar-card">
                            <div className="sidebar-red-header">
                                <div className="talent-avatar-box"><FaUserAlt size={35} /></div>
                                <h5 className="mt-3 mb-0 text-uppercase">{selectedApp.talent_name}</h5>
                                <small className="opacity-75">Talent Profile</small>
                            </div>
                            <div className="sidebar-body p-3">
                                {profileLoading ? (
                                    <div className="text-center py-5"><Spinner size="sm" variant="danger" /></div>
                                ) : (
                                    <>
                                        <h6 className="sidebar-section-title">Talent Attributes</h6>
                                        {/* ✅ 4 COLUMN GRID FOR CANDIDATE DATA */}
                                        <div className="meta-grid-4-col">
                                            <div className="meta-cell"><label>Age</label><span>{tp.age || '---'}</span></div>
                                            <div className="meta-cell"><label>Gender</label><span>{tp.gender || '---'}</span></div>
                                            <div className="meta-cell"><label>Height</label><span>{tp.height || '---'} cm</span></div>
                                            <div className="meta-cell"><label>Weight</label><span>{tp.weight || '---'} kg</span></div>
                                            <div className="meta-cell"><label>Skin</label><span>{tp.skinType || '---'}</span></div>
                                            <div className="meta-cell"><label>Eyes</label><span>{tp.eyeColour || '---'}</span></div>
                                            <div className="meta-cell"><label>Hair Type</label><span>{tp.hairType || '---'}</span></div>
                                            <div className="meta-cell"><label>Hair Color</label><span>{tp.hairColor || '---'}</span></div>
                                            <div className="meta-cell"><label>Body</label><span>{tp.bodyType || '---'}</span></div>
                                            <div className="meta-cell"><label>Nationality</label><span>{tp.nationality || '---'}</span></div>
                                            <div className="meta-cell"><label>Education</label><span>{tp.educationLevel || '---'}</span></div>
                                            <div className="meta-cell"><label>Currency</label><span>{tp.preferredCurrency || '---'}</span></div>
                                        </div>

                                        <h6 className="sidebar-section-title mt-4">Biography</h6>
                                        <p className="small text-white-50">{tp.aboutYourself || "No description provided."}</p>
                                        
                                        <Button variant="outline-danger" size="sm" className="w-100 mt-3 rounded-pill" onClick={() => navigate(`/profile/${selectedApp.talent_id}`)}>KNOW MORE</Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </Col>

                    {/* RIGHT SIDE: WIZARD CONTENT */}
                    <Col lg={7}>
                        <div className="wizard-review-container">
                            <div className="wizard-stepper-row">
                                {steps.map((step, i) => (
                                    <div key={i} className={`step-item-custom ${currentStep >= i + 1 ? 'active' : ''}`} onClick={() => setCurrentStep(i + 1)}>
                                        <div className="step-circle-custom">{i + 1}</div>
                                        <div className="step-label-custom">{step}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="red-strip-bar">
                                <span>{steps[currentStep-1]}</span>
                                <span>Step {currentStep} of 4</span>
                            </div>
                            <div className="wizard-body-content p-4">
                                {currentStep === 1 && (
                                    <div className="animate-fade-in">
                                        <h6 className="text-danger fw-bold text-uppercase mb-3">Application Details</h6>
                                        <div className="meta-info-dark-card p-3 rounded">
                                            <Row className="g-3">
                                                <Col xs={6}><label className="lbl">Project</label><div className="val">{selectedApp.c_project_name}</div></Col>
                                                <Col xs={6}><label className="lbl">Role</label><div className="val">{selectedApp.c_role_name}</div></Col>
                                                <Col xs={6}><label className="lbl">Month</label><div className="val">{selectedApp.c_B_Month}</div></Col>
                                                <Col xs={6}><label className="lbl">Mode</label><div className="val">{selectedApp.c_audition_type}</div></Col>
                                            </Row>
                                        </div>
                                    </div>
                                )}
                                {currentStep === 2 && (
                                    <div className="animate-fade-in">
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <h6 className="text-danger small fw-bold text-uppercase mb-2"><FaFileAlt className="me-2"/>Scene Details</h6>
                                                <div className="script-view-box p-3 rounded" dangerouslySetInnerHTML={{ __html: selectedApp.c_roleScene || 'N/A' }} />
                                            </Col>
                                            <Col md={6}>
                                                <h6 className="text-danger small fw-bold text-uppercase mb-2"><FaVideo className="me-2"/>Audition Script</h6>
                                                <div className="script-view-box p-3 rounded" dangerouslySetInnerHTML={{ __html: selectedApp.c_roleScript || 'N/A' }} />
                                            </Col>
                                        </Row>
                                    </div>
                                )}
                                {currentStep === 3 && (
                                    <div className="animate-fade-in text-center py-4">
                                        <h6 className="text-danger fw-bold text-uppercase mb-4">Candidate Performance</h6>
                                        <div className="video-placeholder-box p-5 border border-dashed rounded-4">
                                            <FaVideo size={50} className="text-muted mb-3" />
                                            <p className="text-white">Uploaded Video Content Placeholder</p>
                                        </div>
                                    </div>
                                )}
                                {currentStep === 4 && (
                                    <div className="animate-fade-in text-center py-4">
                                        <h6 className="text-white fw-bold text-uppercase mb-4">Set Final Audition Result</h6>
                                        <div className="decision-box mx-auto" style={{maxWidth: '350px'}}>
                                            <Form.Group className="mb-4">
                                                <Form.Select className="custom-input-review" value={decision} onChange={(e) => setDecision(e.target.value)}>
                                                    <option value="">-- Choose Status --</option>
                                                    <option value="SELECTED">Selected</option>
                                                    <option value="RESUBMIT">Resubmit</option>
                                                    <option value="REJECTED">Rejected</option>
                                                </Form.Select>
                                            </Form.Group>
                                            <Button variant="danger" className="w-100 py-2 fw-bold" onClick={handleUpdateStatus}>FINALIZE STATUS</Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="wizard-footer d-flex justify-content-between p-3 border-top border-white-10">
                                <Button variant="outline-light" size="sm" disabled={currentStep === 1} onClick={() => setCurrentStep(prev => prev - 1)}>PREVIOUS</Button>
                                {currentStep < 4 && <Button variant="danger" size="sm" onClick={() => setCurrentStep(prev => prev + 1)}>NEXT</Button>}
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        );
    }

    return (
        <div className="manage-applications-container p-3 animate-fade-in">
            <MetaTags title="Audition List - BookMyAudition" />
            <div className="page-header mb-4">
                <h5 className="text-uppercase fw-bold color-b7343a pb-2">Audition List</h5>
            </div>
            {/* List View with original columns preserved */}
            <Row className="mb-3 align-items-center g-3">
                <Col xs={12} md={6} className="d-flex align-items-center">
                    <span className="me-2 small text-white">Show</span>
                    <Form.Select size="sm" style={{ width: '80px' }} value={entriesPerPage} onChange={(e) => setEntriesPerPage(Number(e.target.value))}>
                        <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option>
                    </Form.Select>
                    <span className="ms-2 small text-white">entries</span>
                </Col>
                <Col xs={12} md={6}>
                    <InputGroup size="sm" className="ms-auto" style={{ maxWidth: '300px' }}>
                        <Form.Control placeholder="Search talent or role..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        <InputGroup.Text><FaSearch /></InputGroup.Text>
                    </InputGroup>
                </Col>
            </Row>

            <div className="d-none d-lg-block shadow-sm rounded border-0 overflow-hidden">
                <Table hover responsive className="mb-0 audition-data-table">
                    <thead>
                        <tr>
                            <th className="text-center">#</th>
                            <th>Month</th>
                            <th>Date</th>
                            <th>Talent Name</th> 
                            <th>Role Title</th> 
                            <th>Audition Type</th>
                            <th>Verify Status</th>
                            <th>Status</th>
                            <th className="text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentData.map((item, index) => (
                            <tr key={item.id}>
                                <td className="text-center">{(currentPage - 1) * entriesPerPage + index + 1}</td>
                                <td>{item.c_B_Month}</td>
                                <td>{item.c_S_Date}</td>
                                <td><span className="fw-bold text-danger">{item.talent_name}</span></td>
                                <td>{item.c_role_name}</td>
                                <td><Badge bg="info">{item.c_audition_type}</Badge></td>
                                <td>{item.c_V_Status}</td>
                                <td><span className="small fw-bold">{item.c_R_Status}</span></td>
                                <td className="text-center">
                                    <Button variant="link" className="p-0 text-white me-2" onClick={() => handleViewApp(item)}><FaEye /></Button>
                                    <Button variant="link" className="p-0 text-danger"><FaTrash /></Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
            {/* Mobile View with Cards */}
            <div className="d-lg-none">
                {currentData.map((item, index) => (
                    <Card key={item.id} className="mb-3 app-mobile-card">
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between mb-2">
                                <span className="fw-bold text-white">{item.talent_name}</span>
                                <Badge bg="danger">{item.c_R_Status}</Badge>
                            </div>
                            <div className="small text-white-50 mb-3">{item.c_role_name}</div>
                            <div className="d-flex gap-2">
                                <Button size="sm" variant="outline-light" className="w-50" onClick={() => handleViewApp(item)}><FaEye /> View</Button>
                                <Button size="sm" variant="outline-danger" className="w-50"><FaTrash /> Delete</Button>
                            </div>
                        </Card.Body>
                    </Card>
                ))}
            </div>
        </div>
    );
};

export default ManageApplicationsPage;